import React, { useEffect } from 'react'
import styled from 'styled-components'
import { FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material'

import LinkButton from '../../components/LinkButton'
import SlideContainer from '../../components/Layout/SlideContainer'

import breakpoints from '../../constants/breakpoints'
import theme from '../../theme'
import { FeedbackAnswer, Slide } from '../../types'
import { getLocalStorage, getNextSkipRoute, setLocalStorage } from '../../utils'
import Readspeaker from '../../components/Readspeaker'
import { formControlLabel, radioGroup, formControl } from './styles'

interface Props {
  routeId: string
  basePath: string
  nextLink: string
  button: string
  subtitle: string
  title: string
  description?: string
  descriptionAlign?: string
  feedbackAnswers: FeedbackAnswer[]
  routeIndex?: string
  routes: Slide[]
  onSkipRoute: (path: string) => void
}

const FeedbackQuestion: React.FC<Props> = ({
  routeId,
  basePath,
  button,
  nextLink,
  subtitle,
  title,
  description,
  descriptionAlign,
  feedbackAnswers,
  routeIndex,
  routes,
  onSkipRoute,
}) => {
  const answersInStorage = getLocalStorage('answers')
  const foundAnswer =
    answersInStorage && answersInStorage.find((answer) => answer.routeId === routeId)
  const [value, setValue] = React.useState({
    routeId: routeId,
    answer: foundAnswer ? foundAnswer.answer[0] : '',
    shownResponse: foundAnswer ? foundAnswer.feedback : '',
    isConditionalRoute:
      foundAnswer && foundAnswer.isConditionalRoute ? foundAnswer.isConditionalRoute : false,
  })

  const feedbackColor = feedbackAnswers.find(
    (item) => item.correctOption === true && item.title === value.answer,
  )
    ? theme.green
    : feedbackAnswers.find((item) => item.correctOption === false)
      ? theme.red
      : theme.yellow

  useEffect(() => {
    const nextRoute = routeIndex && getNextSkipRoute(routeIndex, routes)
    nextRoute && onSkipRoute(nextRoute.path)
  }, [])

  const handleChange = (answer: FeedbackAnswer, isConditionalRoute = false) => {
    setValue({
      routeId,
      answer: answer.title,
      shownResponse: answer.feedback,
      isConditionalRoute,
    })
  }

  const handleLocalStorage = () => {
    setLocalStorage(
      {
        routeId,
        answer: [value.answer],
        feedback: value.shownResponse,
        isConditionalRoute: value.isConditionalRoute,
        feedbackColor: feedbackColor,
      },
      basePath,
    )
  }

  return (
    <SlideContainer>
      <Content className="text-formatted">
        <FixedWidth>
          <Subtitle>{subtitle}</Subtitle>
          <h1 id={title.replace(/[^A-Z0-9]+/gi, '_')}>{title}</h1>
          <Readspeaker />
          {description && (
            <Description
              $align={descriptionAlign}
              dangerouslySetInnerHTML={{ __html: description }}
            ></Description>
          )}
        </FixedWidth>

        <FormControl component="fieldset" sx={[formControl]}>
          <legend className="sr-only">{title}</legend>
          <RadioGroup
            value={value.answer}
            aria-labelledby={title.replace(/[^A-Z0-9]+/gi, '_')}
            name="customized-radios"
            sx={[radioGroup]}
          >
            {feedbackAnswers.map((answer, index) => {
              return (
                <ButtonContainer key={index} checked={value.answer === answer.title}>
                  <FormControlLabel
                    value={answer.title}
                    control={<Radio color="primary" />}
                    label={answer.title}
                    sx={[formControlLabel]}
                    onChange={() => handleChange(answer)}
                  />
                </ButtonContainer>
              )
            })}
          </RadioGroup>
        </FormControl>

        {value.shownResponse && (
          <Response
            className="rs_skip"
            role="region"
            aria-live="polite"
            $feedbackColor={feedbackColor}
            dangerouslySetInnerHTML={{ __html: value.shownResponse }}
          ></Response>
        )}

        {nextLink && (
          <Next>
            <LinkButton onClick={handleLocalStorage} to={nextLink}>
              {button}
            </LinkButton>
          </Next>
        )}
      </Content>
    </SlideContainer>
  )
}

const FixedWidth = styled.div`
  max-width: 700px;
`

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: center;
  padding-bottom: 40px;
`

const Subtitle = styled.span`
  font-size: 1rem;
  font-weight: bold;
`

const Description = styled.div<{ $align: string | undefined }>`
  margin-top: 40px;
  text-align: ${({ $align }) => $align || 'left'};
`

const ButtonContainer = styled.div<{ checked: boolean }>`
  width: 100%;
  margin: 5px 0;

  @media (min-width: ${breakpoints.md}) {
    margin: 0 20px;
    width: 180px;
  }

  > .MuiFormControlLabel-root {
    & svg {
      width: 30px;
      height: 30px;
    }
  }

  ${({ checked, theme }) =>
    checked &&
    `
    > .MuiFormControlLabel-root {
      background-color: ${theme.blue};
      color: white;
      box-shadow: 0 2px 10px 0 rgba(30, 30, 30, 0.4);
      border-color: ${theme.blue};

      & svg {
        fill: white;
      }
    }
  `};
`

const Response = styled.div<{ $feedbackColor: string }>`
  padding: 14px 32px;
  margin-top: 60px;
  text-align: left;
  background-color: ${theme.lighterGrey};
  border-left: 3px solid ${({ $feedbackColor }) => $feedbackColor};

  @media (min-width: ${breakpoints.md}) {
    width: 700px;
  }

  & ul {
    margin-top: 0;
  }
`

const Next = styled.div`
  margin: 60px auto 0;
  width: 100%;
  max-width: 700px;
  position: fixed;
  box-shadow: 0 -10px 27px 9px rgba(0, 0, 0, 0.06);
  bottom: 0;
  background: white;

  & a {
    margin: 10px 0;
    width: 202px;
    padding: 15px;
  }

  @media (min-width: ${breakpoints.sm}) {
    position: inherit;
    box-shadow: none;

    & a {
      width: 100%;
    }
  }
`

export default FeedbackQuestion
